import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { mfiApi } from "../../api";
import { normalizeCouponError, isCouponRequestCancelled } from "./coupons/couponErrors";
import { COLORS } from "./coupons/couponUi";
import SharedCouponCard from "./coupons/SharedCouponCard";
import MedicalCheckupCoupon from "./coupons/MedicalCheckupCoupon";

export default function FamilyCouponsPage() {
  const { clientId, packageId, familyId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const reqIdRef = useRef(0);

  const missingParams = !clientId || !packageId || !familyId;
  const backPath =
    clientId && packageId
      ? `/coordinator/mfi/families/${encodeURIComponent(clientId)}/${encodeURIComponent(packageId)}`
      : "/coordinator/mfi";

  const load = useCallback(async () => {
    if (!familyId) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const reqId = ++reqIdRef.current;

    setLoading(true);
    setError(null);
    try {
      const res = await mfiApi.getFamilyCoupons(familyId, { signal: controller.signal });
      if (reqId !== reqIdRef.current) return;
      setData(res);
      setError(null);
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      if (isCouponRequestCancelled(err)) return;
      const normalized = normalizeCouponError(err);
      if (!normalized) return;
      setError(normalized);
      setData(null);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [familyId]);

  useEffect(() => {
    setData(null);
    load();
    return () => {
      reqIdRef.current += 1;
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  const family = data?.family || null;
  const coupons = Array.isArray(data?.coupons) ? data.coupons : [];

  const panelStyle = {
    backgroundColor: COLORS.errorBg,
    border: `1px solid ${COLORS.errorBorder}`,
    borderRadius: 12,
    padding: 20,
    textAlign: "center",
  };
  const secondaryBtn = {
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.mediumGrey}`,
    borderRadius: 6,
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: COLORS.lightGrey, padding: "20px 16px 40px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <header
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <button
            type="button"
            onClick={() => navigate(backPath)}
            aria-label="Back to families"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.mediumGrey}`,
              borderRadius: 6,
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 500,
              color: COLORS.darkText,
            }}
          >
            <ArrowLeft size={15} aria-hidden="true" />
            Back
          </button>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: COLORS.darkText }}>
              Family Coupons
            </h1>
            <p style={{ margin: "2px 0 0 0", fontSize: 13, color: COLORS.mediumText }}>
              <strong style={{ color: COLORS.aquaBlue }}>{familyId || "—"}</strong>
              {family?.family_head?.name ? <> · {family.family_head.name}</> : null}
              {family?.package?.name ? <> · {family.package.name}</> : null}
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading || missingParams}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              backgroundColor: COLORS.aquaBlue,
              color: COLORS.white,
              border: "none",
              borderRadius: 6,
              padding: "8px 16px",
              cursor: loading || missingParams ? "not-allowed" : "pointer",
              fontSize: 13,
              fontWeight: 600,
              opacity: loading || missingParams ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} aria-hidden="true" />
            Refresh
          </button>
        </header>

        {missingParams ? (
          <div role="alert" style={panelStyle}>
            <p style={{ color: COLORS.errorText, fontSize: 14, margin: 0 }}>
              Missing family information. Please open this page from the families list.
            </p>
            <button type="button" onClick={() => navigate(backPath)} style={{ ...secondaryBtn, marginTop: 12 }}>
              Back
            </button>
          </div>
        ) : null}

        {!missingParams && loading ? (
          <div
            role="status"
            aria-label="Loading family coupons"
            style={{
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.mediumGrey}`,
              borderRadius: 12,
              padding: "32px 0",
              textAlign: "center",
              color: COLORS.mediumText,
              fontSize: 13,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 26,
                height: 26,
                border: `3px solid ${COLORS.mediumGrey}`,
                borderTopColor: COLORS.aquaBlue,
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                marginBottom: 8,
              }}
            />
            <div>Loading family coupons…</div>
            <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
          </div>
        ) : null}

        {!missingParams && !loading && error ? (
          <div role="alert" style={panelStyle}>
            <p style={{ color: COLORS.errorText, fontSize: 14, margin: 0 }}>{error.message}</p>
            <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 8 }}>
              <button
                type="button"
                onClick={load}
                style={{ ...secondaryBtn, backgroundColor: COLORS.aquaBlue, color: COLORS.white, border: "none", fontWeight: 600 }}
              >
                Retry
              </button>
              <button type="button" onClick={() => navigate(backPath)} style={secondaryBtn}>
                Back
              </button>
            </div>
          </div>
        ) : null}

        {!missingParams && !loading && !error ? (
          coupons.length === 0 ? (
            <div
              style={{
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.mediumGrey}`,
                borderRadius: 12,
                padding: 24,
                textAlign: "center",
              }}
            >
              <p style={{ margin: 0, color: COLORS.mediumText, fontSize: 14 }}>
                No coupon has been created for this family yet
              </p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
                gap: 16,
              }}
            >
              {coupons.map((c, i) => {
                if (!c || typeof c !== "object") return null;
                const key = c.type || c.name || i;
                if (c.type === "MEDICAL_CHECKUP") {
                  return <MedicalCheckupCoupon key={key} coupon={c} />;
                }
                return <SharedCouponCard key={key} coupon={c} familyId={familyId} />;
              })}
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
